(() => {
  const runtimeKey = "__prospectSplashRuntime";
  window[runtimeKey]?.destroy?.();
  const runtimeListeners = [];
  let activeRuntime = null;

  const listen = (target, type, handler, options) => {
    if (!target) return;
    if (typeof target.addEventListener === 'function') {
      target.addEventListener(type, handler, options);
      runtimeListeners.push(() => target.removeEventListener?.(type, handler, options));
      return;
    }
    if (typeof target.addListener === 'function') {
      target.addListener(handler);
      runtimeListeners.push(() => target.removeListener?.(handler));
    }
  };

  const createRuntime = () => {
    const localListeners = [];
    const listenLocal = (target, type, handler, options) => {
      if (!target) return;
      if (typeof target.addEventListener === 'function') {
        target.addEventListener(type, handler, options);
        localListeners.push(() => target.removeEventListener?.(type, handler, options));
        return;
      }
      if (typeof target.addListener === 'function') {
        target.addListener(handler);
        localListeners.push(() => target.removeListener?.(handler));
      }
    };

  const root = document.documentElement;
  const body = document.body;

  const revealEls = [...document.querySelectorAll('.reveal')];
  const header = document.querySelector('.site-header');
  const responsivePictures = [...document.querySelectorAll('picture[data-responsive-picture]')];
  const deferredPictures = responsivePictures.filter((picture) => picture.hasAttribute('data-lazy-picture'));
  const deferredPictureLoads = new WeakMap();
  const pendingRevealTargets = new WeakSet();
  let deferredArtworkStarted = false;
  let responsiveSizeFrame = 0;
  let motionQuery = null;
  let revealObserver = null;
  let motionEnabled = false;
  let initialFrame = 0;
  let revealFrame = 0;
  let initialActivationComplete = false;
  let recoveryListenersAttached = false;
  let deferredArtworkObserver = null;
  let deferredArtworkScanFrame = 0;
  let deferredArtworkTimerId = 0;

  function clearMotionPending() {
    if (window.__prospectMotionSafetyTimer) {
      window.clearTimeout(window.__prospectMotionSafetyTimer);
      window.__prospectMotionSafetyTimer = 0;
    }
    root.classList.remove('motion-pending');
  }

  function updateResponsivePictureSize(picture) {
    const image = picture.querySelector('img');
    if (!image) return;
    const renderedWidth = Number.parseFloat(window.getComputedStyle(image).width);
    if (!Number.isFinite(renderedWidth) || renderedWidth <= 0) return;
    const sizes = `${Math.ceil(renderedWidth)}px`;
    picture.querySelectorAll('source').forEach((source) => {
      source.sizes = sizes;
    });
    if (image.hasAttribute('srcset') || image.hasAttribute('data-srcset')) image.sizes = sizes;
  }

  function refreshResponsivePictureSizes() {
    responsiveSizeFrame = 0;
    responsivePictures.forEach(updateResponsivePictureSize);
  }

  function scheduleResponsivePictureSizes() {
    if (responsiveSizeFrame) return;
    responsiveSizeFrame = requestAnimationFrame(refreshResponsivePictureSizes);
  }

  function waitForArtworkImage(image) {
    if (image.complete) return Promise.resolve(image.naturalWidth > 0);
    return new Promise((resolve) => {
      const settle = () => resolve(image.naturalWidth > 0);
      listenLocal(image, 'load', settle, { once: true });
      listenLocal(image, 'error', settle, { once: true });
    });
  }

  function activateDeferredPicture(picture) {
    const existing = deferredPictureLoads.get(picture);
    if (existing) return existing;

    updateResponsivePictureSize(picture);
    picture.querySelectorAll('source[data-srcset]').forEach((source) => {
      source.srcset = source.dataset.srcset;
      source.removeAttribute('data-srcset');
    });
    const image = picture.querySelector('img');
    if (!image) return Promise.resolve(false);
    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      image.removeAttribute('data-srcset');
    }
    if (image.dataset.src) {
      image.loading = 'lazy';
      image.fetchPriority = 'low';
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    }
    picture.dataset.artworkState = 'loading';

    const load = waitForArtworkImage(image).then((loaded) => {
      picture.dataset.artworkState = loaded ? 'loaded' : 'error';
      return loaded;
    });
    deferredPictureLoads.set(picture, load);
    return load;
  }

  function startDeferredArtworkFor(container) {
    const pictures = [...container.querySelectorAll('picture[data-lazy-picture]')];
    return Promise.all(pictures.map(activateDeferredPicture));
  }

  function updateDeferredArtworkState() {
    if (deferredPictures.length === 0) {
      root.dataset.deferredArtwork = 'none';
      return;
    }
    const states = deferredPictures.map((picture) => picture.dataset.artworkState || 'waiting');
    if (states.some((state) => state === 'error')) {
      root.dataset.deferredArtwork = 'error';
    } else if (states.every((state) => state === 'loaded')) {
      root.dataset.deferredArtwork = 'loaded';
    } else if (states.some((state) => state === 'loading' || state === 'loaded')) {
      root.dataset.deferredArtwork = 'loading';
    } else {
      root.dataset.deferredArtwork = 'waiting';
    }
  }

  function pictureIsNearViewport(picture) {
    const rect = picture.getBoundingClientRect();
    const margin = Math.max(window.innerHeight || 0, 500);
    return rect.bottom >= -margin && rect.top <= (window.innerHeight || 0) + margin;
  }

  function activatePicturesNearViewport() {
    deferredArtworkScanFrame = 0;
    deferredPictures
      .filter((picture) => !deferredPictureLoads.has(picture) && pictureIsNearViewport(picture))
      .forEach((picture) => activateDeferredPicture(picture).then(updateDeferredArtworkState));
    updateDeferredArtworkState();
  }

  function scheduleDeferredArtworkScan() {
    if (deferredArtworkScanFrame) return;
    deferredArtworkScanFrame = requestAnimationFrame(activatePicturesNearViewport);
  }

  function startDeferredArtwork() {
    if (deferredArtworkStarted) return;
    deferredArtworkStarted = true;
    refreshResponsivePictureSizes();
    updateDeferredArtworkState();

    if (typeof window.IntersectionObserver === 'function') {
      try {
        deferredArtworkObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            deferredArtworkObserver?.unobserve(entry.target);
            activateDeferredPicture(entry.target).then(updateDeferredArtworkState);
          });
        }, { rootMargin: '500px 0px' });
        deferredPictures.forEach((picture) => deferredArtworkObserver.observe(picture));
      } catch {
        deferredArtworkObserver = null;
      }
    }

    if (!deferredArtworkObserver) {
      listenLocal(window, 'scroll', scheduleDeferredArtworkScan, { passive: true });
      listenLocal(window, 'resize', scheduleDeferredArtworkScan, { passive: true });
      activatePicturesNearViewport();
    }
  }

  function scheduleDeferredArtwork() {
    if (deferredArtworkTimerId) return;
    deferredArtworkTimerId = window.setTimeout(() => {
      deferredArtworkTimerId = 0;
      startDeferredArtwork();
    }, 0);
  }

  root.dataset.deferredArtwork = deferredPictures.length > 0 ? 'waiting' : 'none';
  if (document.readyState === 'complete') {
    scheduleDeferredArtwork();
  } else {
    listenLocal(window, 'load', scheduleDeferredArtwork, { once: true });
  }
  listenLocal(window, 'resize', scheduleResponsivePictureSizes, { passive: true });

  try {
    if (typeof window.matchMedia === 'function') {
      motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    }
  } catch {
    motionQuery = null;
  }

  function markVisible(element) {
    if (!element) return;
    element.classList.add('is-visible');
    try {
      revealObserver?.unobserve(element);
    } catch {
      // A failed observer must never strand visible content or later setup.
    }
    if (allRevealsVisible()) detachRevealRecovery();
  }

  function waitForArtworkBeforeReveal(asset) {
    if (!asset?.matches('.asset-placeholder')) return null;
    const images = [...asset.querySelectorAll('picture[data-responsive-picture] img')];
    if (images.length === 0) return null;
    const deferredLoading = startDeferredArtworkFor(asset);
    if (images.every((image) => image.complete && image.naturalWidth > 0)) return null;
    const loading = Promise.all([
      deferredLoading,
      ...images.map(waitForArtworkImage),
    ]);

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve();
      };
      const timeout = window.setTimeout(finish, 900);
      loading.then(finish, finish);
    });
  }

  function revealWrapper(element) {
    if (!element || element.classList.contains('is-visible') || pendingRevealTargets.has(element)) return;
    const readiness = waitForArtworkBeforeReveal(element);
    if (!readiness) {
      markVisible(element);
      return;
    }
    pendingRevealTargets.add(element);
    readiness.then(() => markVisible(element));
  }

  function revealPair(section) {
    if (pendingRevealTargets.has(section)) return;
    const copy = section.querySelector('.story-copy.reveal');
    const asset = section.querySelector('.asset-placeholder.reveal');
    const readiness = waitForArtworkBeforeReveal(asset);
    if (!readiness) {
      markVisible(asset);
      markVisible(copy);
      return;
    }
    pendingRevealTargets.add(section);
    readiness.then(() => {
      markVisible(asset);
      markVisible(copy);
    });
  }

  function revealSectionFor(node) {
    if (!(node instanceof Element)) return;
    const section = node.closest('.story-section');
    if (!section) return;

    const reveal = node.closest('.reveal');
    if (window.innerWidth < 900 && reveal && section.contains(reveal)) {
      revealWrapper(reveal);
      return;
    }

    revealPair(section);
  }

  function revealHashTarget() {
    if (!window.location.hash) return;
    let targetId;
    try {
      targetId = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      targetId = window.location.hash.slice(1);
    }
    const target = document.getElementById(targetId);
    const section = target?.closest('.story-section');
    if (!section) return;
    markVisible(section.querySelector('.story-copy.reveal'));
    revealWrapper(section.querySelector('.asset-placeholder.reveal'));
  }

  function allRevealsVisible() {
    return revealEls.every((element) => element.classList.contains('is-visible'));
  }

  function detachRevealRecovery() {
    if (!recoveryListenersAttached) return;
    window.removeEventListener('scroll', scheduleRevealCheck);
    window.removeEventListener('resize', scheduleRevealCheck);
    window.removeEventListener('pageshow', scheduleRevealCheck);
    recoveryListenersAttached = false;
  }

  function attachRevealRecovery() {
    if (recoveryListenersAttached) return;
    window.addEventListener('scroll', scheduleRevealCheck, { passive: true });
    window.addEventListener('resize', scheduleRevealCheck, { passive: true });
    window.addEventListener('pageshow', scheduleRevealCheck);
    recoveryListenersAttached = true;
  }

  function settleMotion() {
    motionEnabled = false;
    initialActivationComplete = true;
    if (initialFrame) {
      cancelAnimationFrame(initialFrame);
      initialFrame = 0;
    }
    if (revealFrame) {
      cancelAnimationFrame(revealFrame);
      revealFrame = 0;
    }
    try {
      revealObserver?.disconnect();
    } catch {
      // The visible fallback does not depend on a clean observer shutdown.
    }
    revealObserver = null;
    detachRevealRecovery();
    clearMotionPending();
    root.classList.remove('has-motion');
    revealEls.forEach(markVisible);
    revealEls.forEach((element) => {
      try {
        element?.getAnimations?.({ subtree: true }).forEach((animation) => animation.cancel());
      } catch {
        // Reduced motion still settles through the capability-class removal.
      }
    });
  }

  function revealInView() {
    if (!motionEnabled || !initialActivationComplete) return;
    const revealLine = window.innerHeight * 0.9;
    revealEls.forEach((element) => {
      if (!element.classList.contains('is-visible') && element.getBoundingClientRect().top < revealLine) {
        revealSectionFor(element);
      }
    });
    if (allRevealsVisible()) detachRevealRecovery();
  }

  function scheduleRevealCheck() {
    if (!motionEnabled || !initialActivationComplete || revealFrame || allRevealsVisible()) return;
    revealFrame = requestAnimationFrame(() => {
      revealFrame = 0;
      revealInView();
    });
  }

  function beginInitialActivation() {
    if (!motionEnabled || initialFrame) return;
    initialFrame = requestAnimationFrame(() => {
      initialFrame = 0;
      if (!motionEnabled) return;
      initialFrame = requestAnimationFrame(() => {
        initialFrame = 0;
        if (!motionEnabled) return;
        initialActivationComplete = true;
        revealInView();
      });
    });
  }

  function setupMotion() {
    if (root.dataset.motionExpired === 'true'
      || !motionQuery
      || motionQuery.matches
      || typeof window.IntersectionObserver !== 'function') {
      settleMotion();
      return;
    }

    let observer;
    try {
      observer = new window.IntersectionObserver((entries) => {
        if (!motionEnabled || !initialActivationComplete) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) revealSectionFor(entry.target);
        });
      }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    } catch {
      settleMotion();
      return;
    }

    try {
      revealObserver = observer;
      revealEls.forEach((element) => revealObserver.observe(element));
      initialActivationComplete = false;
      motionEnabled = true;
      root.classList.add('has-motion');
      clearMotionPending();
      attachRevealRecovery();
      beginInitialActivation();
    } catch {
      settleMotion();
    }
  }

  setupMotion();
  revealHashTarget();
  listenLocal(window, 'hashchange', revealHashTarget);
  listenLocal(document, 'focusin', (event) => {
    const reveal = event.target instanceof Element ? event.target.closest('.reveal') : null;
    if (reveal) markVisible(reveal);
  });

  const mobileMenus = [...document.querySelectorAll('.mobile-menu')];
  const mobileMenuOpeningFrames = new WeakMap();
  const mobileMenuCloseStates = new WeakMap();
  let mobileMenuCloseSequence = 0;

  const syncNavigationLabel = (menu) => {
    const summary = menu.querySelector('summary');
    summary?.setAttribute('aria-label', menu.open ? 'Close navigation' : 'Open navigation');
  };

  const cancelMobileMenuOpening = (menu) => {
    const state = mobileMenuOpeningFrames.get(menu);
    if (state) {
      if (state.firstFrame) cancelAnimationFrame(state.firstFrame);
      if (state.secondFrame) cancelAnimationFrame(state.secondFrame);
      mobileMenuOpeningFrames.delete(menu);
    }
    menu.classList.remove('is-opening');
  };

  const clearMobileMenuCloseState = (menu) => {
    const state = mobileMenuCloseStates.get(menu);
    if (!state) return;
    window.clearTimeout(state.timeoutId);
    state.nav?.removeEventListener('transitionend', state.onTransitionEnd);
    if (state.settleFrame) cancelAnimationFrame(state.settleFrame);
    mobileMenuCloseStates.delete(menu);
  };

  const cancelMobileMenuClose = (menu) => {
    clearMobileMenuCloseState(menu);
    menu.classList.remove('is-closing');
  };

  const finishMobileMenuClose = (menu, token) => {
    const state = mobileMenuCloseStates.get(menu);
    if (!state || state.token !== token) return;
    clearMobileMenuCloseState(menu);
    menu.classList.remove('is-closing');
    if (state.nav?.contains(document.activeElement)) {
      menu.querySelector('summary')?.focus({ preventScroll: true });
    }
    menu.open = false;
    syncNavigationLabel(menu);
  };

  const openMobileMenu = (menu) => {
    cancelMobileMenuClose(menu);
    cancelMobileMenuOpening(menu);
    if (menu.open) {
      syncNavigationLabel(menu);
      return;
    }

    menu.classList.add('is-opening');
    menu.open = true;
    syncNavigationLabel(menu);
    if (motionQuery?.matches) {
      menu.classList.remove('is-opening');
      return;
    }

    const state = { firstFrame: 0, secondFrame: 0 };
    mobileMenuOpeningFrames.set(menu, state);
    state.firstFrame = requestAnimationFrame(() => {
      state.firstFrame = 0;
      if (mobileMenuOpeningFrames.get(menu) !== state || !menu.open) return;
      state.secondFrame = requestAnimationFrame(() => {
        state.secondFrame = 0;
        if (mobileMenuOpeningFrames.get(menu) !== state || !menu.open) return;
        mobileMenuOpeningFrames.delete(menu);
        menu.classList.remove('is-opening');
      });
    });
  };

  const closeMobileMenu = (menu, { immediate = false, restoreFocus = false } = {}) => {
    if (!menu.open) return;
    const existingState = mobileMenuCloseStates.get(menu);
    if (existingState) {
      if (restoreFocus) menu.querySelector('summary')?.focus({ preventScroll: true });
      if (immediate) finishMobileMenuClose(menu, existingState.token);
      return;
    }
    cancelMobileMenuOpening(menu);

    const summary = menu.querySelector('summary');
    if (restoreFocus) summary?.focus({ preventScroll: true });

    const nav = menu.querySelector('nav');
    if (immediate || motionQuery?.matches || !nav) {
      menu.classList.remove('is-closing');
      menu.open = false;
      syncNavigationLabel(menu);
      return;
    }

    menu.classList.add('is-closing');
    const token = ++mobileMenuCloseSequence;
    const startedAt = performance.now();
    const expectedMotionMs = longestTransitionMs(nav);
    const onTransitionEnd = (event) => {
      if (event.target !== nav) return;
      const activeState = mobileMenuCloseStates.get(menu);
      if (!activeState || activeState.token !== token || activeState.settleFrame) return;
      activeState.settleFrame = requestAnimationFrame(() => {
        activeState.settleFrame = 0;
        const elapsed = performance.now() - startedAt;
        if (elapsed + 24 >= expectedMotionMs && !elementHasActiveMotion(nav)) {
          finishMobileMenuClose(menu, token);
        }
      });
    };
    const state = { token, nav, timeoutId: 0, settleFrame: 0, onTransitionEnd };
    mobileMenuCloseStates.set(menu, state);
    nav.addEventListener('transitionend', onTransitionEnd);
    state.timeoutId = window.setTimeout(
      () => finishMobileMenuClose(menu, token),
      Math.max(expectedMotionMs + 80, 80),
    );
  };

  mobileMenus.forEach((menu) => {
    const summary = menu.querySelector('summary');
    const nav = menu.querySelector('nav');
    syncNavigationLabel(menu);
    listenLocal(summary, 'click', (event) => {
      event.preventDefault();
      if (menu.classList.contains('is-closing')) {
        cancelMobileMenuClose(menu);
        syncNavigationLabel(menu);
      } else if (menu.open) {
        closeMobileMenu(menu);
      } else {
        openMobileMenu(menu);
      }
    });
    listenLocal(menu, 'toggle', () => {
      if (!menu.open) {
        cancelMobileMenuOpening(menu);
        cancelMobileMenuClose(menu);
      }
      syncNavigationLabel(menu);
    });
    listenLocal(nav, 'click', (event) => {
      const action = event.target instanceof Element
        ? event.target.closest('a[href], button:not([disabled])')
        : null;
      if (action && nav.contains(action)) closeMobileMenu(menu);
    });
  });

  listenLocal(document, 'click', (event) => {
    const eventPath = event.composedPath();
    mobileMenus.forEach((menu) => {
      if (menu.open && !eventPath.includes(menu)) closeMobileMenu(menu);
    });
  });

  const headerScrolledClass = 'is-scrolled';
  const headerSurfaceThreshold = 12;
  let headerSurfaceFrame = 0;

  const updateHeaderSurface = () => {
    headerSurfaceFrame = 0;
    header?.classList.toggle(headerScrolledClass, Math.max(window.scrollY, 0) > headerSurfaceThreshold);
  };

  const scheduleHeaderSurface = () => {
    if (headerSurfaceFrame) return;
    headerSurfaceFrame = requestAnimationFrame(updateHeaderSurface);
  };

  listenLocal(window, 'scroll', scheduleHeaderSurface, { passive: true });
  listenLocal(window, 'pageshow', scheduleHeaderSurface);
  updateHeaderSurface();

  let mobileHeaderQuery = null;
  try {
    mobileHeaderQuery = window.matchMedia('(max-width: 1023px)');
  } catch {
    mobileHeaderQuery = null;
  }

  if (mobileHeaderQuery) {
    const handleMobileHeaderBreakpoint = () => {
      if (!mobileHeaderQuery.matches) {
        mobileMenus.forEach((menu) => closeMobileMenu(menu, { immediate: true }));
      }
    };
    if (typeof mobileHeaderQuery.addEventListener === 'function') {
      listenLocal(mobileHeaderQuery, 'change', handleMobileHeaderBreakpoint);
    } else if (typeof mobileHeaderQuery.addListener === 'function') {
      listenLocal(mobileHeaderQuery, 'change', handleMobileHeaderBreakpoint);
    }
    handleMobileHeaderBreakpoint();
  }

  const modals = [...document.querySelectorAll('.modal')];
  const signupModal = document.getElementById('signup-modal');
  const athleteModal = document.getElementById('athlete-modal');
  const modalOwners = new WeakMap();
  modals.forEach((modal) => {
    modalOwners.set(modal, modal.parentElement);
    if (modal.parentElement !== body) body.append(modal);
  });
  const openers = [...document.querySelectorAll('.js-open-modal')];
  const athleteOpeners = [...document.querySelectorAll('.js-open-athlete-modal')];
  const closers = [...document.querySelectorAll('.js-close-modal')];
  const pageBehindModals = [...body.children].filter((element) => !element.classList.contains('modal') && element.tagName !== 'SCRIPT');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const pageState = new Map();
  const modalCloseStates = new WeakMap();
  const modalOpenFocusFrames = new WeakMap();
  const modalOpeners = new WeakMap();
  let modalCloseSequence = 0;

  function setPageBehindModalHidden(hidden) {
    if (hidden) {
      if (pageState.size > 0) return;
      pageBehindModals.forEach((element) => {
        pageState.set(element, {
          inert: element.hasAttribute('inert'),
          ariaHidden: element.getAttribute('aria-hidden'),
        });
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
      });
      return;
    }

    pageState.forEach((state, element) => {
      if (!state.inert) element.removeAttribute('inert');
      if (state.ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', state.ariaHidden);
      }
    });
    pageState.clear();
  }

  function focusableElements(modal) {
    return [...modal.querySelectorAll(focusableSelector)].filter((element) => {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  function containModalFocus(event, modal) {
    const focusable = focusableElements(modal);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!modal.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function clearModalCloseState(modal) {
    const state = modalCloseStates.get(modal);
    if (!state) return;
    window.clearTimeout(state.timeoutId);
    modal.removeEventListener('transitionend', state.onTransitionEnd);
    if (state.settleFrame) cancelAnimationFrame(state.settleFrame);
    modalCloseStates.delete(modal);
  }

  function cancelModalOpenFocus(modal) {
    const state = modalOpenFocusFrames.get(modal);
    if (!state) return;
    if (state.firstFrame) cancelAnimationFrame(state.firstFrame);
    if (state.secondFrame) cancelAnimationFrame(state.secondFrame);
    window.clearTimeout(state.postClickTimeoutId);
    window.clearTimeout(state.timeoutId);
    modalOpenFocusFrames.delete(modal);
  }

  function focusModalContent(modal) {
    if (!modal.classList.contains('is-open') || modal.hasAttribute('inert')) return false;
    if (modal.contains(document.activeElement)) return true;
    const firstField = modal.querySelector('input:not([type="hidden"]):not([disabled])');
    const target = firstField || focusableElements(modal)[0];
    if (!target) return false;
    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus();
    }
    return modal.contains(document.activeElement);
  }

  function scheduleModalOpenFocus(modal) {
    cancelModalOpenFocus(modal);
    focusModalContent(modal);
    const state = { firstFrame: 0, secondFrame: 0, postClickTimeoutId: 0, timeoutId: 0 };
    modalOpenFocusFrames.set(modal, state);
    // A real pointer activation can apply native button focus after its click
    // listener returns. Reassert dialog focus in the next task, before paint,
    // then keep the frame checks as protection against asynchronous inert work.
    state.postClickTimeoutId = window.setTimeout(() => {
      state.postClickTimeoutId = 0;
      if (modalOpenFocusFrames.get(modal) !== state) return;
      focusModalContent(modal);
    }, 0);
    state.firstFrame = requestAnimationFrame(() => {
      state.firstFrame = 0;
      if (modalOpenFocusFrames.get(modal) !== state) return;
      focusModalContent(modal);
      state.secondFrame = requestAnimationFrame(() => {
        state.secondFrame = 0;
        if (modalOpenFocusFrames.get(modal) !== state) return;
        focusModalContent(modal);
      });
    });
    state.timeoutId = window.setTimeout(() => {
      if (modalOpenFocusFrames.get(modal) !== state) return;
      modalOpenFocusFrames.delete(modal);
      focusModalContent(modal);
    }, 64);
  }

  function transitionTimeMs(value) {
    return value.split(',').map((part) => {
      const time = part.trim();
      if (time.endsWith('ms')) return Number.parseFloat(time) || 0;
      if (time.endsWith('s')) return (Number.parseFloat(time) || 0) * 1000;
      return 0;
    });
  }

  function longestTransitionMs(element) {
    const style = window.getComputedStyle(element);
    const durations = transitionTimeMs(style.transitionDuration);
    const delays = transitionTimeMs(style.transitionDelay);
    return durations.reduce((longest, duration, index) => (
      Math.max(longest, duration + (delays[index % delays.length] || 0))
    ), 0);
  }

  function modalCloseMotionMs(modal) {
    const movingParts = [modal, ...modal.querySelectorAll('.modal-backdrop, .modal-panel')];
    return Math.max(0, ...movingParts.map(longestTransitionMs));
  }

  function elementHasActiveMotion(element) {
    try {
      return element.getAnimations({ subtree: true }).some((animation) => animation.playState === 'running');
    } catch {
      return false;
    }
  }

  function finishModalClose(modal, token) {
    const state = modalCloseStates.get(modal);
    if (!state || state.token !== token) return;
    clearModalCloseState(modal);
    modal.classList.remove('is-closing');
  }

  function cancelModalClose(modal) {
    clearModalCloseState(modal);
    modal.classList.remove('is-closing');
  }

  function hideModalImmediately(modal) {
    cancelModalOpenFocus(modal);
    cancelModalClose(modal);
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    try {
      modal.getAnimations?.({ subtree: true }).forEach((animation) => animation.cancel());
    } catch {
      // Replacement remains hidden even if animation cancellation is unavailable.
    }
  }

  function closeModalVisually(modal) {
    if (!modal || !modal.classList.contains('is-open')) return;
    cancelModalOpenFocus(modal);
    clearModalCloseState(modal);
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');

    if (motionQuery?.matches) {
      modal.classList.remove('is-closing');
      return;
    }

    modal.classList.add('is-closing');
    const token = ++modalCloseSequence;
    const startedAt = performance.now();
    const expectedMotionMs = modalCloseMotionMs(modal);
    const onTransitionEnd = (event) => {
      if (!modal.contains(event.target)) return;
      const state = modalCloseStates.get(modal);
      if (!state || state.token !== token || state.settleFrame) return;
      state.settleFrame = requestAnimationFrame(() => {
        state.settleFrame = 0;
        const elapsed = performance.now() - startedAt;
        if (elapsed + 24 >= expectedMotionMs && !elementHasActiveMotion(modal)) {
          finishModalClose(modal, token);
        }
      });
    };
    const state = { token, timeoutId: 0, settleFrame: 0, onTransitionEnd };
    modalCloseStates.set(modal, state);
    modal.addEventListener('transitionend', onTransitionEnd);
    state.timeoutId = window.setTimeout(
      () => finishModalClose(modal, token),
      Math.max(expectedMotionMs + 80, 80),
    );
  }

  function openModal(modal) {
    if (!modal) return;
    cancelModalClose(modal);
    cancelModalOpenFocus(modal);
    const activeModal = modals.find((candidate) => candidate.classList.contains('is-open'));
    const activeElement = document.activeElement;
    const mobileMenu = activeElement instanceof Element ? activeElement.closest('.mobile-menu') : null;
    const restorationTarget = mobileMenu?.querySelector('summary') || activeElement;
    modalOpeners.set(modal, activeModal ? modalOpeners.get(activeModal) : restorationTarget);
    modals.forEach((otherModal) => {
      if (otherModal !== modal) hideModalImmediately(otherModal);
    });
    setPageBehindModalHidden(true);
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    body.style.overflow = 'hidden';
    // Focus is an accessibility state, not part of the visual fade. Move it
    // synchronously, then verify on the next frame after the click has settled.
    scheduleModalOpenFocus(modal);
  }

  function closeModal(modal) {
    if (!modal || !modal.classList.contains('is-open')) return;
    const opener = modalOpeners.get(modal);
    closeModalVisually(modal);
    if (!modals.some(openModalEl => openModalEl.classList.contains('is-open'))) {
      body.style.overflow = '';
      setPageBehindModalHidden(false);
    }
    if (opener?.isConnected && !opener.closest?.('[inert]')) {
      try {
        opener.focus({ preventScroll: true });
      } catch {
        opener.focus?.();
      }
    }
  }

  if (motionQuery) {
    const settleForReducedMotion = (event) => {
      if (!event.matches) {
        scheduleHeaderSurface();
        return;
      }
      settleMotion();
      mobileMenus.forEach((menu) => {
        if (menu.classList.contains('is-closing')) {
          closeMobileMenu(menu, { immediate: true });
        } else {
          cancelMobileMenuOpening(menu);
        }
      });
      modals.forEach((modal) => {
        const state = modalCloseStates.get(modal);
        if (state) finishModalClose(modal, state.token);
        if (modal.classList.contains('is-open') && !modal.hasAttribute('inert')) {
          scheduleModalOpenFocus(modal);
        }
      });
    };
    if (typeof motionQuery.addEventListener === 'function') {
      listenLocal(motionQuery, 'change', settleForReducedMotion);
    } else if (typeof motionQuery.addListener === 'function') {
      listenLocal(motionQuery, 'change', settleForReducedMotion);
    }
  }

  openers.forEach(button => listenLocal(button, 'click', (event) => {
    if (button.matches('a[href]')) event.preventDefault();
    openModal(signupModal);
  }));
  athleteOpeners.forEach(button => listenLocal(button, 'click', (event) => {
    if (button.matches('a[href]')) event.preventDefault();
    openModal(athleteModal);
  }));
  closers.forEach(button => listenLocal(button, 'click', () => closeModal(button.closest('.modal'))));
  listenLocal(document, 'focusin', (event) => {
    const openModalEl = modals.find((modal) => modal.classList.contains('is-open'));
    if (openModalEl && !openModalEl.contains(event.target)) focusModalContent(openModalEl);
  });
  listenLocal(window, 'keydown', (event) => {
    const openModalEl = modals.find(modal => modal.classList.contains('is-open'));
    if (event.key === 'Tab' && openModalEl) {
      containModalFocus(event, openModalEl);
      return;
    }
    if (event.key === 'Escape') {
      if (openModalEl) {
        event.preventDefault();
        closeModal(openModalEl);
        return;
      }
      const openMenus = mobileMenus.filter((menu) => menu.open);
      if (openMenus.length > 0) {
        event.preventDefault();
        openMenus.forEach((menu, index) => closeMobileMenu(menu, { restoreFocus: index === 0 }));
      }
    }
  });

  return {
    destroy({ removeOrphanedModals = false, sectionTarget = null } = {}) {
      localListeners.splice(0).forEach((remove) => remove());
      if (responsiveSizeFrame) cancelAnimationFrame(responsiveSizeFrame);
      if (deferredArtworkScanFrame) cancelAnimationFrame(deferredArtworkScanFrame);
      if (deferredArtworkTimerId) window.clearTimeout(deferredArtworkTimerId);
      deferredArtworkObserver?.disconnect?.();
      deferredArtworkObserver = null;
      if (revealObserver) revealObserver.disconnect?.();
      revealObserver = null;
      if (initialFrame) cancelAnimationFrame(initialFrame);
      if (revealFrame) cancelAnimationFrame(revealFrame);
      initialFrame = 0;
      revealFrame = 0;
      detachRevealRecovery();
      mobileMenus.forEach((menu) => {
        cancelMobileMenuOpening(menu);
        clearMobileMenuCloseState(menu);
        menu.classList.remove('is-opening', 'is-closing');
        if (menu.open) {
          menu.open = false;
          syncNavigationLabel(menu);
        }
      });
      modals.forEach((modal) => {
        const sectionId = modal.dataset.splashSectionId;
        const owner = modalOwners.get(modal);
        const isOrphan = removeOrphanedModals
          && modal.parentElement === body
          && ((owner && sectionTarget?.contains?.(owner))
            || (sectionId && sectionTarget?.dataset?.sectionId === sectionId));
        hideModalImmediately(modal);
        if (isOrphan) modal.remove();
      });
      body.style.overflow = '';
      setPageBehindModalHidden(false);
      root.classList.remove('has-motion', 'motion-pending');
      header?.classList.remove(headerScrolledClass);
      delete root.dataset.deferredArtwork;
    },
  };
  };

  const splashSectionSelector = '.splash-section, .splash-intake-section, .splash-intake';
  const isSplashSectionTarget = (target) => Boolean(
    target?.matches?.(splashSectionSelector)
      || target?.querySelector?.(splashSectionSelector),
  );

  const mount = (sectionTarget = null) => {
    activeRuntime?.destroy({
      removeOrphanedModals: Boolean(sectionTarget),
      sectionTarget,
    });
    activeRuntime = createRuntime();
  };

  const onSectionLoad = (event) => {
    if (isSplashSectionTarget(event.target)) mount(event.target);
  };
  const onSectionUnload = (event) => {
    if (!isSplashSectionTarget(event.target)) return;
    activeRuntime?.destroy({ removeOrphanedModals: true, sectionTarget: event.target });
    activeRuntime = null;
  };

  listen(document, 'shopify:section:load', onSectionLoad);
  listen(document, 'shopify:section:unload', onSectionUnload);
  if (document.readyState === 'loading') {
    listen(document, 'DOMContentLoaded', () => mount());
  } else {
    mount();
  }

  window[runtimeKey] = {
    initialize: mount,
    destroy() {
      activeRuntime?.destroy();
      activeRuntime = null;
      runtimeListeners.splice(0).forEach((remove) => remove());
      delete window[runtimeKey];
    },
  };
})();
